// packages/ui/src/components/StatusBar.tsx
// Always-visible bottom bar with node indicators, session count, and budget meter

export interface NodeStatus {
  id: string
  name: string
  status: 'connected' | 'disconnected'
}

export interface BudgetInfo {
  used: number
  total: number
}

export interface StatusBarProps {
  nodes: NodeStatus[]
  activeSessions: number
  budget: BudgetInfo
}

function getBudgetColor(used: number, total: number): string {
  const pct = total > 0 ? (used / total) * 100 : 0
  if (pct > 95) return 'bg-red-500'
  if (pct >= 80) return 'bg-yellow-500'
  return 'bg-blue-500'
}

export function StatusBar({ nodes, activeSessions, budget }: StatusBarProps) {
  const pct = budget.total > 0 ? Math.min((budget.used / budget.total) * 100, 100) : 0

  return (
    <div
      data-testid="status-bar"
      className="flex items-center justify-between px-4 py-1.5 bg-gray-900 border-t border-gray-800 text-xs"
    >
      {/* Left: Node connection dots */}
      <div className="flex items-center gap-3">
        {nodes.map((node) => (
          <div
            key={node.id}
            data-testid={`node-dot-${node.id}`}
            className="flex items-center gap-1.5"
            title={`${node.name}: ${node.status}`}
          >
            <span
              data-testid={`dot-indicator-${node.id}`}
              className={`w-2 h-2 rounded-full ${
                node.status === 'connected' ? 'bg-green-500' : 'bg-red-500'
              }`}
            />
            <span className="text-gray-400">{node.name}</span>
          </div>
        ))}
      </div>

      {/* Center: Active session count */}
      <div data-testid="session-count" className="text-gray-400">
        {activeSessions} {activeSessions === 1 ? 'session' : 'sessions'}
      </div>

      {/* Right: Budget usage meter */}
      <div data-testid="budget-meter" className="flex items-center gap-2">
        <span className="text-gray-400">
          {budget.used} / {budget.total}
        </span>
        <div className="w-20 bg-gray-700 rounded-full h-1.5">
          <div
            data-testid="budget-bar-fill"
            className={`h-1.5 rounded-full transition-all ${getBudgetColor(budget.used, budget.total)}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  )
}
